import React, { useState, useEffect } from "react";
import { Box, Button, Modal, Typography, Snackbar, StepButton, useTheme, CircularProgress, Tooltip } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import SystemControlPanelDetails from "./SystemControlPanelDetails";
import Arrangements from "./Arrangements";
import UnitSelection from "./UnitSelection";
import { projectManageAxios as axios } from '../../../../utils/AxiosUtility';
import { useSearchParams } from "react-router-dom";

const defaultPanelDetails = {
  domesticInternational: "Domestic (120V)",
  primaryCommunicationsConnectionType: "Dedicated Wan",
  backupCommunicationsConnectionType: "Dedicated Wan",
  pointTopointPackage: false,
  pointTopointDetails: "",
  primaryEbutton: false,
  primaryEbuttonDetails: "",
  sensorPackageType: "180",
  plumbingInfrastructureType: "Wet",
  valveTypeSpecified: "EB20 Vic x Vic",
  fireAlarmPanelInterconnect: false,
  makeModelFireAlarmPanel: "",
  riserRoomCamera: false,
  makeModelPreactionValve: "",
  makeModelPreactionPanel: "",
  suppressionSolutionInductor: false,
  suppressionSolutionSelection: "F500",
  backupPowerGenerator: false,
  coldWeatherPackage: false,
  coldWeatherBsu: "Insulated Box Bottom",
  conveyorStop: false,
  arrangementResponsibleForConveyorStop: "Arrangement 1",
  conveyorStopDetectionDevice: "Thermal",
  conveyorStopFireRoverOutput: "Output 1",
  conveyorSirenStrobe: "Yes (D4100)",
  conveyorStopSirenReset: false,
  conveyorStopSirenResetInput: "Input 3",
  outputConfigurations: [{ id: 0, value: "FACP Spray On Interconnect", circuitType: "NO (Normally Open)", 
    circuitMethod: "Momentary", resetMethod: "", notes: "" },],
  inputConfigurations: [{ id: 0, value: "Heat Detector", circuitType: "NO (Normally Open)", 
    circuitMethod: "Momentary", resetMethod: "", panelAction: "Alarm", reportToCs: false, notes: "" }],
  auxInputConfigurations: [{ id: 0, value: "Heat Detector", notes: "" }],
  viewingStation: false,
  viewingStationDetail: "Beelink computer & 32'' monitor with desktop stand",
  viewingStationLocation: "",
  customConfigurations: [{ id: 0, notes: "" }],
  productionNoteConfigurations: [{ id: 0, notes: "" }],
};

const defaultArrangement = {
  location: "",
  name: "",
  mountingHeight: "",
  ebutton: false,
  ebuttonInstructions: "",
  detectionZones: [],
  suppressionZones: [],
};

export default function DeviceConfig({ isProject }) {
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [units, setUnits] = useState([{ id: 1, value: "", active: true }]);
  const [selectedUnitId, setSelectedUnitId] = useState(1);
  const theme = useTheme();
  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();
  const [unitConfigs, setUnitConfigs] = useState({
    1: {
      panelDetails: { ...defaultPanelDetails },
      arrangements: [{ ...defaultArrangement }],
    },
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const fetchData = () => {

    const endpoint = isProject ? `/api/DeviceConfig/project/getDeviceConfigInfo/${searchParams.get("id")}` 
      : `/api/DeviceConfig/getDeviceConfigInfo/${searchParams.get("id")}`;
    setLoading(true);
    axios.get(endpoint)
      .then((response) => {
        const { units, unitConfigs, panelDetails, arrangements } = response.data;

        setUnits(units);
        setUnitConfigs(unitConfigs);
        setUnitConfigs((prevConfigs) => {
          const newConfigs = { ...prevConfigs };
          units.forEach((unit) => {
            if (!(unit.id in newConfigs)) {
              newConfigs[unit.id] = {
                panelDetails: { ...panelDetails },
                arrangements: [...arrangements],
              };
            }
          });
          return newConfigs;
        });
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        alert("Error retrieving device config data.");
        setLoading(false);
      });
  }

  useEffect(() => {
    setUnitConfigs((prevConfigs) => {
      const newConfigs = { ...prevConfigs };
      units.forEach((unit) => {
        if (!(unit.id in newConfigs)) {
          newConfigs[unit.id] = {
            panelDetails: { ...defaultPanelDetails },
            arrangements: [{ ...defaultArrangement }],
          };
        }
      });
      return newConfigs;
    });
  }, [units]);

  const handleCopyUnitConfig = (newUnitId, sourceUnitId) => {
    setUnitConfigs((prevConfigs) => {
      if (prevConfigs[sourceUnitId]) {
        const copiedConfig = JSON.parse(
          JSON.stringify(prevConfigs[sourceUnitId])
        );
        return { ...prevConfigs, [newUnitId]: copiedConfig };
      }
      return prevConfigs;
    });
  };

  const handleUnitSelect = (unitId) => {
    setSelectedUnitId(unitId);
  };

  const handleOnPanelDetailChange = (details) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId] || {
        panelDetails: { ...defaultPanelDetails },
        arrangements: [{ ...defaultArrangement }],
      };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          panelDetails: {
            ...currentConfig.panelDetails,
            ...details,
          },
        },
      };
    });
  };

  const handleAddArrangement = () => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: [
            ...currentConfig.arrangements,
            { ...defaultArrangement },
          ],
        },
      };
    });
  };

  const handleRemoveArrangement = (index) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = currentConfig.arrangements.filter(
        (_, i) => i !== index
      );
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const handleChangeArrangement = (index, field, value) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = [...currentConfig.arrangements];
      newArrangements[index] = { ...newArrangements[index], [field]: value };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const handleAddDetectionZone = (arrangementIndex) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = [...currentConfig.arrangements];
      const detectionZoneDefaults = {
        enabled: false,
        zoneLocation: "Interior",
        thermalDetection: "FLIR A50 - 95 Degree",
        flameDetection: "OFD - Low setting",
        smokeAnalytics: false,
        indicatorLight: false,
      };
      newArrangements[arrangementIndex] = {
        ...newArrangements[arrangementIndex],
        detectionZones: [
          ...newArrangements[arrangementIndex].detectionZones,
          detectionZoneDefaults,
        ],
      };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const handleRemoveDetectionZone = (arrangementIndex, zoneIndex) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = [...currentConfig.arrangements];
      const newDetectionZones = newArrangements[arrangementIndex].detectionZones.filter(
        (_, i) => i !== zoneIndex
      );
      newArrangements[arrangementIndex] = {
        ...newArrangements[arrangementIndex],
        detectionZones: newDetectionZones,
      };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const handleChangeDetectionZone = (arrangementIndex, zoneIndex, field, value) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = [...currentConfig.arrangements];
      const newDetectionZones = [...newArrangements[arrangementIndex].detectionZones];
      newDetectionZones[zoneIndex] = {
        ...newDetectionZones[zoneIndex],
        [field]: value,
      };
      newArrangements[arrangementIndex] = {
        ...newArrangements[arrangementIndex],
        detectionZones: newDetectionZones,
      };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const handleAddSuppressionZone = (arrangementIndex) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = [...currentConfig.arrangements];
      const defaultSuppressionZone = {
        enabled: false,
        suppressionMastTowerBuilding: "Standard Mast",
        monitorNozzleConfig: "Sidewinder EXM2",
        nozzle: "7/8'' Smoothbore",
        riserIndicatorLight: false,
        fdConnect: false,
        connectType: "",
      };
      newArrangements[arrangementIndex] = {
        ...newArrangements[arrangementIndex],
        suppressionZones: [
          ...newArrangements[arrangementIndex].suppressionZones,
          defaultSuppressionZone,
        ],
      };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const handleRemoveSuppressionZone = (arrangementIndex, zoneIndex) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = [...currentConfig.arrangements];
      const newSuppressionZones = newArrangements[arrangementIndex].suppressionZones.filter(
        (_, i) => i !== zoneIndex
      );
      newArrangements[arrangementIndex] = {
        ...newArrangements[arrangementIndex],
        suppressionZones: newSuppressionZones,
      };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const handleChangeSuppressionZone = (arrangementIndex, zoneIndex, field, value) => {
    setUnitConfigs((prevConfigs) => {
      const currentConfig = prevConfigs[selectedUnitId];
      const newArrangements = [...currentConfig.arrangements];
      const newSuppressionZones = [...newArrangements[arrangementIndex].suppressionZones];
      newSuppressionZones[zoneIndex] = {
        ...newSuppressionZones[zoneIndex],
        [field]: value,
      };
      newArrangements[arrangementIndex] = {
        ...newArrangements[arrangementIndex],
        suppressionZones: newSuppressionZones,
      };
      return {
        ...prevConfigs,
        [selectedUnitId]: {
          ...currentConfig,
          arrangements: newArrangements,
        },
      };
    });
  };

  const steps = [
    "Unit Selection",
    "System / Panel Configurator",
    "Arrangements",
  ];

  const handleModalOpen = () => setOpen(true);
  const handleModalClose = () => setOpen(false);
  const handleSnackbarClose = () => setSnackbarOpen(false);

  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  const saveDeviceConfig = () => {
    const preSaleLeadId = searchParams.get("id");

    const deviceConfigData = {
      preSaleLeadId,
      units: units.map((unit) => ({
        id: unit.id,
        value: unit.value,
        active: unit.active,
      })),
      unitConfigs: Object.entries(unitConfigs).reduce((acc, [unitId, config]) => {
        acc[unitId] = {
          panelDetails: {
            ...config.panelDetails,
            outputConfigurations: config.panelDetails.outputConfigurations.map((output, index) => ({
              ...output,
              configNo: index + 1,
            })),
            inputConfigurations: config.panelDetails.inputConfigurations.map((input, index) => ({
              ...input,
              configNo: index + 1,
            })),
            auxInputConfigurations: config.panelDetails.auxInputConfigurations.map((auxInput, index) => ({
              ...auxInput,
              configNo: index + 1,
            })),
            productionNoteConfigurations: config.panelDetails.productionNoteConfigurations.map((note, index) => ({
              ...note,
              configNo: index + 1,
            })),
            customConfigurations: config.panelDetails.customConfigurations.map((custom, index) => ({
              ...custom,
              configNo: index + 1,
            })),
          },
          arrangements: config.arrangements.map((arrangement, arrangementIndex) => ({
            ...arrangement,
            arrangementNo: arrangementIndex + 1,
            detectionZones: arrangement.detectionZones.map((zone, detectionIndex) => ({
              ...zone,
              detectionNo: detectionIndex + 1,
            })),
            suppressionZones: arrangement.suppressionZones.map((zone, suppressionIndex) => ({
              ...zone,
              suppressionNo: suppressionIndex + 1,
            })),
          })),
        };

        return acc;
      }, {}),
    };

    setLoading(true);

    axios.post("/api/DeviceConfig/saveDeviceConfigInfo", deviceConfigData)
      .then(() => {
        setSnackbarOpen(true);
      })
      .catch((error) => {
        console.error(error);
        alert(error.response?.data || "Error saving device configuration. Please try again.");
      })
      .finally(() => {
        fetchData();
        handleModalClose();
      });
  };

  const currentPanelDetails = unitConfigs[selectedUnitId]
    ? unitConfigs[selectedUnitId].panelDetails
    : { ...defaultPanelDetails };

  const currentArrangements = unitConfigs[selectedUnitId]
    ? unitConfigs[selectedUnitId].arrangements
    : [{ ...defaultArrangement }];

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleModalOpen}
        sx={{
          color: theme.palette.mode === "dark" ? "white" : "red",
          borderColor: theme.palette.mode === "dark" ? "white" : "red",
          "&:hover": {
            borderColor: theme.palette.mode === "dark" ? "white" : "red",
          },
        }}
      >
        Device Configuration
      </Button>
      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="Device Configuration"
        aria-describedby="Device Configuration"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 1000,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            overflowY: "scroll",
            maxHeight: "90vh",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 2,
              height: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <Stepper
              nonLinear
              activeStep={activeStep}
              sx={{ width: "90%", paddingTop: "3%", paddingBottom: "2%" }}
            >
              {steps.map((label, index) => (
                <Step key={label} completed={completed[index]}>
                  <Tooltip title={units.length === 0 ? "Please add a unit to proceed" : ""}>
                    <span>
                      <StepButton
                        color="inherit"
                        onClick={units.length > 0 ? handleStep(index) : null}
                        disabled={units.length === 0}
                      >
                        {label}
                      </StepButton>
                    </span>
                  </Tooltip>
                </Step>
              ))}
            </Stepper>

            <Typography variant="h6" sx={{ mb: 1 }}>
              {(() => {
                const selectedUnit = units.find((u) => u.id === selectedUnitId);
                return selectedUnit
                  ? `Selected Unit ${selectedUnit.id}: ${selectedUnit.value || "N/A"}`
                  : "No Unit Selected";
              })()}
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            {allStepsCompleted() ? (
              <>
                <Typography sx={{ mt: 2, mb: 1 }}>
                  All Device Configuration Steps Completed
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Box sx={{ flex: "1 1 auto" }} />
                  <Button onClick={handleReset}>Reset</Button>
                </Box>
              </>
            ) : (
              <>
                <Typography sx={{ mt: 2, mb: 1, py: 1 }}>
                  {activeStep === 0 ? (
                    <UnitSelection
                    units={units}
                    onChangeUnits={setUnits}
                    onSelectUnit={handleUnitSelect}
                    onCopyConfig={handleCopyUnitConfig}
                    />
                  ) : activeStep === 1 ? (
                    <SystemControlPanelDetails
                      DomesticInternational={currentPanelDetails.domesticInternational}
                      PrimaryCommunicationsConnectionType={currentPanelDetails.primaryCommunicationsConnectionType}
                      BackupCommunicationsConnectionType={currentPanelDetails.backupCommunicationsConnectionType}
                      PointTopointPackage={currentPanelDetails.pointTopointPackage}
                      PointTopointDetails={currentPanelDetails.pointTopointDetails}
                      PrimaryEbutton={currentPanelDetails.primaryEbutton}
                      PrimaryEbuttonDetails={currentPanelDetails.primaryEbuttonDetails}
                      SensorPackageType={currentPanelDetails.sensorPackageType}
                      PlumbingInfrastructureType={currentPanelDetails.plumbingInfrastructureType}
                      ValveTypeSpecified={currentPanelDetails.valveTypeSpecified}
                      fireAlarmPanelInterconnect={currentPanelDetails.fireAlarmPanelInterconnect}
                      MakeModelFireAlarmPanel={currentPanelDetails.makeModelFireAlarmPanel}
                      ColdWeatherPackage={currentPanelDetails.coldWeatherPackage}
                      ColdWeatherBsu={currentPanelDetails.coldWeatherBsu}
                      ConveyorStop={currentPanelDetails.conveyorStop}
                      ArrangementResponsibleForConveyorStop={currentPanelDetails.arrangementResponsibleForConveyorStop}
                      ConveyorStopDetectionDevice={currentPanelDetails.conveyorStopDetectionDevice}
                      ConveyorStopFireRoverOutput={currentPanelDetails.conveyorStopFireRoverOutput}
                      ConveyorSirenStrobe={currentPanelDetails.conveyorSirenStrobe}
                      ConveyorStopSirenReset={currentPanelDetails.conveyorStopSirenReset}
                      ConveyorStopSirenResetInput={currentPanelDetails.conveyorStopSirenResetInput}
                      RiserRoomCamera={currentPanelDetails.riserRoomCamera}
                      MakeModelPreactionValve={currentPanelDetails.makeModelPreactionValve}
                      MakeModelPreactionPanel={currentPanelDetails.makeModelPreactionPanel}
                      SuppressionSolutionInductor={currentPanelDetails.suppressionSolutionInductor}
                      SuppressionSolutionSelection={currentPanelDetails.suppressionSolutionSelection}
                      BackupPowerGenerator={currentPanelDetails.backupPowerGenerator}
                      OutputConfigurations={currentPanelDetails.outputConfigurations}
                      InputConfigurations={currentPanelDetails.inputConfigurations}
                      AuxInputConfigurations={currentPanelDetails.auxInputConfigurations}
                      ViewingStation={currentPanelDetails.viewingStation}
                      ViewingStationDetail={currentPanelDetails.viewingStationDetail}
                      ViewingStationLocation={currentPanelDetails.viewingStationLocation}
                      CustomConfigurations={currentPanelDetails.customConfigurations}
                      ProductionNoteConfigurations={currentPanelDetails.productionNoteConfigurations}
                      onPanelDetailChange={handleOnPanelDetailChange}
                      selectedUnit={units.find((u) => u.id === selectedUnitId)}
                    />
                  ) : activeStep === 2 ? (
                    <Arrangements
                      arrangements={currentArrangements}
                      handleAddArrangement={handleAddArrangement}
                      handleRemoveArrangement={handleRemoveArrangement}
                      handleChange={handleChangeArrangement}
                      handleAddDetectionZone={handleAddDetectionZone}
                      handleRemoveDetectionZone={handleRemoveDetectionZone}
                      handleChangeDetectionZone={handleChangeDetectionZone}
                      handleAddSuppressionZone={handleAddSuppressionZone}
                      handleRemoveSuppressionZone={handleRemoveSuppressionZone}
                      handleChangeSuppressionZone={handleChangeSuppressionZone}
                      selectedUnit={units[selectedUnitId - 1]}
                    />
                  ) : null}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Button
                    variant="outlined"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>

                  <Box sx={{ flex: "1 1 auto" }} />
                  {loading ? <CircularProgress /> : null}

                  <Tooltip title={units.length === 0 ? "Please add a unit to proceed" : ""}>
                    <span>
                      <Button
                        variant="outlined"
                        onClick={handleNext}
                        sx={{ mr: 1 }}
                        disabled={units.length === 0}
                      >
                        Next
                      </Button>
                    </span>
                  </Tooltip>

                  {activeStep !== steps.length &&
                    (completed[activeStep] ? (
                      <Typography variant="caption" sx={{ display: "inline-block" }}>
                        Step {activeStep + 1} already completed
                      </Typography>
                    ) : (
                      !isProject ? (
                        <Button
                          style={{ color: "green" }}
                          variant="outlined"
                          onClick={saveDeviceConfig}
                          disabled={units.length === 0}
                        >
                          Save
                        </Button>
                      ) : null
                    ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
          Device config saved!
        </MuiAlert>
      </Snackbar>
    </>
  );
}
